/**
 * CashAccountForm — the cash-side form matching the legacy "New account"
 * dialog exactly (see Figma screenshot 1).
 *
 * Writes to `accounts` table with the following schema (unchanged from
 * legacy Accounts.js):
 *   { name, type, balance, currency, last4?, notes?, source: 'manual' }
 *
 * Type options mirror `ACCOUNT_TYPES` from lib/categorize.js so we stay
 * schema-consistent. Credit cards are surfaced here too because they
 * remain rows in the `accounts` table (per legacy design).
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectSeparator } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, HelpCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ACCOUNT_TYPES } from '@/lib/categorize';
import useCurrencyOptions from './useCurrencyOptions';

function isLiabilityType(t) { return t === 'credit_card'; }
function isWalletType(t) {
  return ['wallet', 'cash', 'prepaid', 'mobile_money', 'e_wallet', 'other'].includes(t);
}

export default function CashAccountForm({ account = null, onCreate, onUpdate, onClose, baseCurrency = 'INR', presetType = null }) {
  const { user } = useAuth();
  const editing = !!account;
  const currencyOpts = useCurrencyOptions();

  const [form, setForm] = useState({
    name: '', type: presetType || 'bank', currency: baseCurrency, balance: '', last4: '',
    institution: '', is_joint: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing && account?.raw) {
      const r = account.raw;
      setForm({
        name: r.name || '',
        type: r.type || 'bank',
        currency: r.currency || baseCurrency,
        balance: r.balance != null ? String(r.balance) : '',
        last4: r.last4 || '',
        institution: r.institution || '',
        is_joint: !!r.is_joint,
      });
    } else {
      setForm({
        name: '', type: presetType || 'bank', currency: baseCurrency, balance: '',
        last4: '', institution: '', is_joint: false,
      });
    }
  }, [editing, account, baseCurrency, presetType]);

  const save = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (!form.name.trim()) { toast.error('Please give this a name.'); return; }
    if (!form.currency)    { toast.error('Please choose a currency.'); return; }
    const last4 = (form.last4 || '').trim();
    if (last4 && !/^\d{4}$/.test(last4)) {
      toast.error('Last 4 digits must be exactly 4 numbers (or leave blank).'); return;
    }
    setSaving(true);
    try {
      // The `accounts` table intentionally does NOT have a `notes` column;
      // if we ever add one, we can start sending it here without a schema
      // migration first.
      const payload = {
        name: form.name.trim(),
        type: form.type,
        currency: form.currency,
        balance: form.balance ? Number(form.balance) : 0,
      };
      if (last4) payload.last4 = last4;
      // Phase 86.K — institution / joint live behind a hand-applied migration.
      // Send them when filled and drop them if the columns aren't there yet,
      // rather than failing the whole save.
      const institution = (form.institution || '').trim();
      if (institution) payload.institution = institution;
      if (form.is_joint) payload.is_joint = true;

      // Retry-without-last4 fallback keeps us compatible with pre-Phase-17
      // schemas that don't yet have the `last4` column.
      const tryOnce = async (row) => (editing ? onUpdate('accounts', account.id, row) : onCreate('cash', row, user?.id));
      // When a field can't be stored yet we still save the account — but we say
      // so in ONE toast instead of quietly dropping what the user typed.
      let degraded = null;
      try {
        await tryOnce(payload);
      } catch (err) {
        const msg = `${err?.message || ''} ${err?.code || ''}`;
        const schemaGap = /does not exist|PGRST204|schema cache/i.test(msg);
        if (schemaGap && (institution || form.is_joint)) {
          const { institution: _i, is_joint: _j, ...bare } = payload;
          await tryOnce(bare);
          degraded = 'The account was saved, but the institution and joint-account fields need a database update first.';
        } else if (schemaGap && last4) {
          const { last4: _drop, ...bare } = payload;
          await tryOnce(bare);
          degraded = 'The account was saved without the last-4 digits — that field needs a database update first.';
        } else {
          throw err;
        }
      }
      if (degraded) {
        toast.warning(degraded, { duration: 8000 });
      } else {
        toast.success(editing ? 'Saved.' : 'Added.');
      }
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not save.');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-3" data-testid="accounts-form-cash">
      <div className="space-y-1.5">
        <Label>Name</Label>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Chequing, Savings, Credit card…"
          className="rounded-xl"
          data-testid="account-name-input"
          autoFocus
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <Label>Type</Label>
            {/* Phase 84.23.7 — clarify Cash vs Wallet vs Other. Beta
                users kept asking what the difference was. */}
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="What are these account types?"
                    className="inline-flex items-center justify-center text-muted-foreground/70 hover:text-foreground"
                    data-testid="account-type-help"
                  >
                    <HelpCircle className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[260px] text-xs leading-relaxed">
                  <p><span className="font-semibold">Bank account</span> — savings / current account at a bank.</p>
                  <p className="mt-1"><span className="font-semibold">Credit card</span> — pay-later, tracked as a liability.</p>
                  <p className="mt-1"><span className="font-semibold">Wallet</span> — a stored-value app like Paytm, PhonePe, GPay, Apple/Google Pay.</p>
                  <p className="mt-1"><span className="font-semibold">Cash</span> — physical cash you carry. Add one so cash spends have somewhere to go.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger className="rounded-xl" data-testid="account-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ACCOUNT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Currency</Label>
          <Select value={form.currency || undefined} onValueChange={(v) => setForm({ ...form, currency: v })}>
            <SelectTrigger className="rounded-xl" data-testid="account-currency-select">
              <SelectValue placeholder="Choose currency…" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {currencyOpts.top.map((c) => (
                <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code} — {c.label}</SelectItem>
              ))}
              <SelectSeparator />
              {currencyOpts.rest.map((c) => (
                <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code} — {c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>{isLiabilityType(form.type) ? 'Outstanding balance (optional)' : 'Current balance (optional)'}</Label>
        <Input
          type="number" inputMode="decimal" step="0.01"
          value={form.balance}
          onChange={(e) => setForm({ ...form, balance: e.target.value })}
          placeholder="0"
          className="rounded-xl tabular-nums"
          data-testid="account-balance-input"
        />
        <p className="text-[11px] text-muted-foreground">
          {isLiabilityType(form.type)
            ? <>What you currently <span className="font-medium text-foreground">owe</span> on this card, not the credit limit. We treat this as a liability, so it reduces your net worth.</>
            : 'The money currently sitting in this account.'}
        </p>
      </div>

      {!isLiabilityType(form.type) && (
        <div className="space-y-1.5">
          <Label>Institution (optional)</Label>
          <Input
            value={form.institution}
            onChange={(e) => setForm({ ...form, institution: e.target.value })}
            placeholder="HDFC Bank, Emirates NBD, Paytm…"
            className="rounded-xl"
            data-testid="account-institution-input"
          />
          <p className="text-[11px] text-muted-foreground">
            Shown next to the account type in your list. Leave it blank and we&apos;ll simply
            show the type — we never guess it from the name.
          </p>
        </div>
      )}

      {!isLiabilityType(form.type) && !isWalletType(form.type) && (
        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="account-joint-label">
          <input
            type="checkbox"
            checked={form.is_joint}
            onChange={(e) => setForm({ ...form, is_joint: e.target.checked })}
            className="mt-0.5 h-4 w-4 rounded border-border accent-evergreen"
            data-testid="account-joint-checkbox"
          />
          <span className="text-sm text-charcoal">
            This is a joint account
            <span className="block text-[11px] text-muted-foreground">
              Display only — it doesn&apos;t change any calculation.
            </span>
          </span>
        </label>
      )}

      <div className="space-y-1.5">
        <Label>Last 4 digits (optional)</Label>
        <Input
          inputMode="numeric"
          maxLength={4}
          value={form.last4}
          onChange={(e) => setForm({ ...form, last4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
          placeholder="e.g. 3421"
          className="rounded-xl tabular-nums tracking-widest"
          data-testid="account-last4-input"
        />
        <p className="text-[11px] text-muted-foreground">
          Just the last 4, helps you tell two cards apart. We never ask for the full number, CVV, or expiry.
        </p>
      </div>

      <DialogFooter className="sm:justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onClose} disabled={saving} className="rounded-xl">Cancel</Button>
        <Button type="submit" disabled={saving} className="rounded-xl" data-testid="account-save-button">
          {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
          {editing ? 'Save changes' : 'Save'}
        </Button>
      </DialogFooter>
    </form>
  );
}
